import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, deleteDoc, getDoc, updateDoc, writeBatch, onSnapshot } from "firebase/firestore";
import Lottie from "lottie-react";
import loadingAnimation from "../lottie/loading-animation.json";

function ProfileSection({ auth, db }) {
  const [folders, setFolders] = useState([]);
  const [downloadedFolders, setDownloadedFolders] = useState([]);
  const [openFolderId, setOpenFolderId] = useState(null);
  const [openDownloadedFolderId, setOpenDownloadedFolderId] = useState(null);
  const [folderQuestions, setFolderQuestions] = useState([]);
  const [downloadedFolderQuestions, setDownloadedFolderQuestions] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: 'folder'|'question'|'downloaded', id, folderId?, name?, shareCode?}
  const [userEmailMasked, setUserEmailMasked] = useState("");

  useEffect(() => {
    const fetchFolders = async () => {
      if (!auth.currentUser) return;
      try {
        const userUID = auth.currentUser.uid;
        const userDocRef = doc(db, "users", userUID);
        const userDocSnap = await getDoc(userDocRef);
        let downloadedTopics = [];
        if (userDocSnap.exists()) {
          downloadedTopics = userDocSnap.data().downloadedTopics || [];
        }

        // Fetch 'Your Folders'
        const quizzesRef = collection(db, "userQuizzes");
        const snapshot = await getDocs(quizzesRef);
        const fetchedFolders = snapshot.docs
          .filter((docSnap) => docSnap.data().ownerId === userUID)
          .map((docSnap) => ({
            id: docSnap.id,
            name: docSnap.data().title || docSnap.id,
            shareCode: docSnap.data().shareCode || "",
          }));
        setFolders(fetchedFolders);

        // Fetch 'Downloaded Folders' (exclude 'Your Folders')
        const downloaded = [];
        for (const shareCode of downloadedTopics) {
          const linkDocSnap = await getDoc(doc(db, "shareCodeLinks", shareCode));
          if (!linkDocSnap.exists()) continue;
          const quizId = linkDocSnap.data().linksTo;
          const type = shareCode.startsWith("o") ? "officialQuizzes" : "userQuizzes";
          const quizDocSnap = await getDoc(doc(db, type, quizId));
          if (
            quizDocSnap.exists() &&
            (type === "officialQuizzes" || quizDocSnap.data().ownerId !== userUID)
          ) {
            downloaded.push({
              id: quizDocSnap.id,
              name: quizDocSnap.data().title || quizDocSnap.id,
              shareCode,
              type,
            });
          }
        }
        setDownloadedFolders(downloaded);
      } catch (error) {
        console.error("Failed to fetch folders:", error.message);
      }
    };
    fetchFolders();
  }, [auth, db]);

  const handleOpenFolder = async (folderId) => {
    setOpenFolderId(folderId === openFolderId ? null : folderId);
    if (folderId === openFolderId) {
      setFolderQuestions([]);
      return;
    }
    try {
      const questionsRef = collection(db, "userQuizzes", folderId, "questions");
      const snapshot = await getDocs(questionsRef);
      setFolderQuestions(snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      })));
    } catch (error) {
      setFolderQuestions([]);
    }
  };

  const handleOpenDownloadedFolder = async (folder) => {
    const folderId = folder.id;
    setOpenDownloadedFolderId(folderId === openDownloadedFolderId ? null : folderId);
    if (folderId === openDownloadedFolderId) {
      setDownloadedFolderQuestions([]);
      return;
    }
    try {
      const questionsRef = collection(db, folder.type, folderId, "questions");
      const snapshot = await getDocs(questionsRef);
      setDownloadedFolderQuestions(snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      })));
    } catch (error) {
      setDownloadedFolderQuestions([]);
    }
  };

  const handleLogout = () => {
    auth.signOut();
  };

  const handleDeleteFolder = (folder) => {
    setDeleteTarget({ type: "folder", id: folder.id, name: folder.name });
    setShowConfirmDelete(true);
  };

  const handleDeleteQuestion = (folderId, questionId, questionText) => {
    setDeleteTarget({ type: "question", id: questionId, folderId, name: questionText });
    setShowConfirmDelete(true);
  };

  const handleRemoveDownloadedFolder = (folder) => {
    setDeleteTarget({ type: "downloaded", id: folder.shareCode, name: folder.name, shareCode: folder.shareCode });
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "folder") {
        // Get shareCode before deleting
        const quizDocRef = doc(db, "userQuizzes", deleteTarget.id);
        const quizDocSnap = await getDoc(quizDocRef);
        let shareCode = "";
        if (quizDocSnap.exists()) {
          shareCode = quizDocSnap.data().shareCode || "";
        }

        // Delete all questions in the folder (Firestore does not cascade delete subcollections)
        const questionsRef = collection(db, "userQuizzes", deleteTarget.id, "questions");
        const questionsSnap = await getDocs(questionsRef);
        const batch = writeBatch(db);
        questionsSnap.forEach(q => {
          batch.delete(q.ref);
        });
        // Delete the quiz doc itself
        batch.delete(quizDocRef);
        await batch.commit();

        // Delete shareCodeLinks entry
        if (shareCode) {
          await deleteDoc(doc(db, "shareCodeLinks", shareCode));
        }

        // Remove shareCode from user's downloadedTopics and selectedTopics
        if (auth.currentUser && shareCode) {
          const userDocRef = doc(db, "users", auth.currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const prevDownloaded = userDocSnap.data().downloadedTopics || [];
            const prevSelected = userDocSnap.data().selectedTopics || [];
            await updateDoc(userDocRef, {
              downloadedTopics: prevDownloaded.filter(code => code !== shareCode),
              selectedTopics: prevSelected.filter(code => code !== shareCode),
            });
          }
        }

        // Remove from UI immediately
        setFolders(folders.filter(f => f.id !== deleteTarget.id));
        setDownloadedFolders(downloadedFolders.filter(f => f.id !== deleteTarget.id && f.shareCode !== shareCode));
        if (openFolderId === deleteTarget.id) {
          setOpenFolderId(null);
          setFolderQuestions([]);
        }
        if (openDownloadedFolderId === deleteTarget.id) {
          setOpenDownloadedFolderId(null);
          setDownloadedFolderQuestions([]);
        }
      } else if (deleteTarget.type === "question") {
        await deleteDoc(doc(db, "userQuizzes", deleteTarget.folderId, "questions", deleteTarget.id));
        setFolderQuestions(folderQuestions.filter(q => q.id !== deleteTarget.id));
      } else if (deleteTarget.type === "downloaded") {
        // Remove shareCode from user's downloadedTopics and selectedTopics
        if (auth.currentUser) {
          const userDocRef = doc(db, "users", auth.currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const prevDownloaded = userDocSnap.data().downloadedTopics || [];
            const prevSelected = userDocSnap.data().selectedTopics || [];
            await updateDoc(userDocRef, {
              downloadedTopics: prevDownloaded.filter(code => code !== deleteTarget.id),
              selectedTopics: prevSelected.filter(code => code !== deleteTarget.id),
            });
            setDownloadedFolders(downloadedFolders.filter(f => f.shareCode !== deleteTarget.id));
            if (openDownloadedFolderId && downloadedFolders.find(f => f.shareCode === deleteTarget.id)?.id === openDownloadedFolderId) {
              setOpenDownloadedFolderId(null);
              setDownloadedFolderQuestions([]);
            }
          }
        }
      }
    } catch (error) {
      // Optionally handle error
    }
    setShowConfirmDelete(false);
    setDeleteTarget(null);
  };

  if (!navigator.onLine) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 130px)",
          flexDirection: "column",
          background: "#0F0F0F",
          transform: "translateY(-130px)",
        }}
      >
        <div
          style={{
            overflow: "hidden",
            width: "200px",
            height: "150px", // Cropped height
          }}
        >
          <Lottie
            animationData={loadingAnimation}
            loop
            style={{ width: "200px", height: "200px" }}
          />
        </div>
        <p style={{ color: "#f8f8f5", marginTop: "20px" }}>
          Connect to the internet for this function
        </p>
      </div>
    );
  }

  return (
    <div
      className="w-full max-w-4xl mx-auto text-[#f8f8f5] bg-[#0F0F0F] relative"
      style={{
        padding: "16px 16px 140px 16px", // 16px left/right, 140px bottom
        boxSizing: "border-box",
        minHeight: "100vh",
      }}
    >
      {/* Account label, email (masked) left, logout right */}
      <div className="flex items-center justify-between mt-2 mb-6">
        <h2 className="text-xl font-semibold text-[#f8f8f5]">Account</h2>
        {/* empty for spacing */}
      </div>
      <div className="flex items-center justify-between mb-8">
        <span className="text-[#bdbdb7] font-mono text-sm select-none">{userEmailMasked}</span>
        <button
          onClick={handleLogout}
          className="py-2 px-4 bg-[#232323] hover:bg-[#222] text-[#f8f8f5] rounded-2xl shadow transition-colors duration-300"
        >
          Log Out
        </button>
      </div>
      {/* Your Folders label */}
      <h2 className="text-xl font-semibold mb-4 text-[#f8f8f5]">Your Folders</h2>
      {/* Folders */}
      <div className="space-y-4 mt-2">
        {folders.length === 0 ? (
          <div className="text-[#bdbdb7]">No folders found.</div>
        ) : (
          folders.map((folder) => (
            <div
              key={folder.id}
              className="bg-[#181818] rounded-xl shadow p-4"
            >
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => handleOpenFolder(folder.id)}
              >
                <div>
                  <span className="font-semibold text-[#f8f8f5]">{folder.name}</span>
                  {folder.shareCode && (
                    <span className="ml-3 text-xs text-[#bdbdb7] bg-[#232323] px-2 py-1 rounded">Share Code: {folder.shareCode}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={e => { e.stopPropagation(); handleDeleteFolder(folder); }}
                    className="px-2 py-1 rounded transition-colors duration-200"
                    title="Delete Folder"
                  >
                    {/* Red trash SVG icon */}
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                      <path d="M6 7h12M9 7v10a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V7" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="9" y="3" width="6" height="4" rx="1" fill="#ff4d4f"/>
                    </svg>
                  </button>
                  <span className="text-[#bdbdb7]">
                    <svg
                      className={`inline transition-transform duration-200 ${openFolderId === folder.id ? "rotate-180" : ""}`}
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              </div>
              {openFolderId === folder.id && (
                <div className="mt-4">
                  {folderQuestions.length === 0 ? (
                    <div className="text-[#bdbdb7]">No questions in this folder.</div>
                  ) : (
                    <div>
                      {folderQuestions.map((q, idx) => (
                        <div key={q.id}>
                          <div className="flex items-center">
                            <div className="py-2 px-2 flex-1">
                              <span className="font-medium text-[#f8f8f5]">
                                {idx + 1}. {q.question}
                              </span>
                            </div>
                            <button
                              onClick={() => handleDeleteQuestion(folder.id, q.id, q.question)}
                              className="px-2 py-1 rounded transition-colors duration-200"
                              title="Delete Question"
                            >
                              {/* Red trash SVG icon (same as folder) */}
                              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                                <path d="M6 7h12M9 7v10a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V7" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <rect x="9" y="3" width="6" height="4" rx="1" fill="#ff4d4f"/>
                              </svg>
                            </button>
                          </div>
                          {idx < folderQuestions.length - 1 && (
                            <hr className="border-t border-[#3a3a3a] mx-2" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {/* Downloaded Folders label */}
      <h2 className="text-xl font-semibold mb-4 mt-8 text-[#f8f8f5]">Downloaded Folders</h2>
      <div className="space-y-4 mt-2">
        {downloadedFolders.length === 0 ? (
          <div className="text-[#bdbdb7]">No downloaded folders found.</div>
        ) : (
          downloadedFolders.map((folder) => (
            <div
              key={folder.id}
              className="bg-[#181818] rounded-xl shadow p-4"
            >
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => handleOpenDownloadedFolder(folder)}
              >
                <div>
                  <span className="font-semibold text-[#f8f8f5]">{folder.name}</span>
                  {folder.shareCode && (
                    <span className="ml-3 text-xs text-[#bdbdb7] bg-[#232323] px-2 py-1 rounded">Share Code: {folder.shareCode}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={e => { e.stopPropagation(); handleRemoveDownloadedFolder(folder); }}
                    className="px-2 py-1 rounded transition-colors duration-200"
                    title="Remove from Downloaded"
                  >
                    {/* White trash SVG icon */}
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                      <path d="M6 7h12M9 7v10a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="9" y="3" width="6" height="4" rx="1" fill="#fff"/>
                    </svg>
                  </button>
                  <span className="text-[#bdbdb7]">
                    <svg
                      className={`inline transition-transform duration-200 ${openDownloadedFolderId === folder.id ? "rotate-180" : ""}`}
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              </div>
              {openDownloadedFolderId === folder.id && (
                <div className="mt-4">
                  {downloadedFolderQuestions.length === 0 ? (
                    <div className="text-[#bdbdb7]">No questions in this folder.</div>
                  ) : (
                    <div>
                      {downloadedFolderQuestions.map((q, idx) => (
                        <div key={q.id}>
                          <div className="flex items-center">
                            <div className="py-2 px-2 flex-1">
                              <span className="font-medium text-[#f8f8f5]">
                                {idx + 1}. {q.question}
                              </span>
                            </div>
                          </div>
                          {idx < downloadedFolderQuestions.length - 1 && (
                            <hr className="border-t border-[#3a3a3a] mx-2" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {/* Confirm Delete Modal */}
      {showConfirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-[#232323] p-6 rounded-xl shadow-lg text-center">
            <p className="mb-4 text-[#f8f8f5]">
              {deleteTarget?.type === "downloaded"
                ? <>Are you sure you want to remove <span className="font-semibold">{deleteTarget?.name}</span> (Share Code: <span className="font-mono">{deleteTarget?.shareCode}</span>) from your downloads?</>
                : deleteTarget?.type === "folder"
                ? <>Are you sure you want to delete the folder <span className="font-semibold">{deleteTarget?.name}</span>?</>
                : <>Are you sure you want to delete the question <span className="font-semibold">{deleteTarget?.name}</span>?</>
              }
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={confirmDelete}
                className="py-2 px-6 bg-red-600 hover:bg-red-700 text-white rounded-2xl shadow transition-colors duration-200"
              >
                Delete
              </button>
              <button
                onClick={() => setShowConfirmDelete(false)}
                className="py-2 px-6 bg-[#232323] hover:bg-[#222] text-[#f8f8f5] rounded-2xl shadow transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
}

export default ProfileSection;

// When creating shareCodeLinks (if not already done), ensure ownerId is set
// await setDoc(doc(db, "shareCodeLinks", shareCode), {
//   linksTo: quizId,
//   ownerId: auth.currentUser.uid,
// });
